import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../../lib/api";
import BetaHmIndicator from "../../../components/BetaHmIndicator";
import DeptBreakdownSection from "../../../components/DeptBreakdownSection";

/** Iter-158.3 — Beta H&M scene list (read-only). */
export function BetaHMScenes() {
    const { projectId } = useParams();
    const nav = useNavigate();
    const [scenes, setScenes] = useState([]);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/beta/hm/projects/${projectId}/scenes`);
            setScenes(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(formatApiError(e));
            if (e?.response?.status === 404) nav("/beta");
        } finally {
            setLoading(false);
        }
    }, [projectId, nav]);

    useEffect(() => { refresh(); }, [refresh]);

    return (
        <div data-testid="beta-hm-scenes" className="min-h-screen bg-neutral-950 text-neutral-100">
            <BetaHmIndicator />
            <div className="px-4 py-4 sm:px-8 sm:py-6">
                <header className="max-w-3xl mx-auto mb-6">
                    <Link
                        to={`/beta/projects/${projectId}/hm`}
                        className="tap text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-neutral-200"
                    >
                        ← Hair &amp; Makeup
                    </Link>
                    <h1 className="text-2xl sm:text-3xl font-semibold mt-2">Scenes</h1>
                    <p className="text-[12px] text-neutral-500 mt-1">
                        {scenes.length} {scenes.length === 1 ? "scene" : "scenes"}
                    </p>
                </header>

                <div className="max-w-3xl mx-auto space-y-2">
                    {loading ? (
                        <p className="text-sm text-neutral-500">Loading…</p>
                    ) : scenes.length === 0 ? (
                        <div className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-6 text-center">
                            <p className="text-sm text-neutral-300">No scenes yet.</p>
                        </div>
                    ) : (
                        <ul className="space-y-2">
                            {scenes.map((s) => (
                                <li key={s.id}>
                                    <Link
                                        data-testid={`beta-hm-scene-row-${s.id}`}
                                        to={`/beta/projects/${projectId}/hm/scenes/${s.id}`}
                                        className="tap block rounded-xl border border-neutral-800 bg-neutral-900/60 p-4 hover:bg-neutral-900"
                                    >
                                        <div className="flex items-baseline gap-3">
                                            <span className="text-neutral-500 text-[11px] uppercase tracking-[0.2em] w-14 shrink-0">
                                                {s.number ? `#${s.number}` : `#${s.order}`}
                                            </span>
                                            <span className={`text-[13px] ${s.omitted ? "text-amber-300/70" : "text-neutral-100"}`}>
                                                {s.heading}
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-neutral-500 mt-1 ml-16">
                                            {s.character_count} {s.character_count === 1 ? "character" : "characters"}
                                        </p>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Iter-158.3 — Beta H&M scene detail (read-only). */
export function BetaHMSceneDetail() {
    const { projectId, sceneId } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/beta/hm/projects/${projectId}/scenes/${sceneId}`);
            setData(data);
        } catch (e) {
            toast.error(formatApiError(e));
            if (e?.response?.status === 404) nav(`/beta/projects/${projectId}/hm/scenes`);
        } finally {
            setLoading(false);
        }
    }, [projectId, sceneId, nav]);

    useEffect(() => { refresh(); }, [refresh]);

    if (loading) return <div className="min-h-screen bg-neutral-950 text-neutral-500 p-8 text-sm">Loading…</div>;
    if (!data) return null;

    return (
        <div data-testid="beta-hm-scene-detail" className="min-h-screen bg-neutral-950 text-neutral-100">
            <BetaHmIndicator />
            <div className="px-4 py-4 sm:px-8 sm:py-6">
                <header className="max-w-3xl mx-auto mb-6">
                    <Link
                        to={`/beta/projects/${projectId}/hm/scenes`}
                        className="tap text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-neutral-200"
                    >
                        ← Scenes
                    </Link>
                    <div className="mt-2 flex items-baseline gap-3">
                        <span className="text-neutral-500 text-[11px] uppercase tracking-[0.2em]">
                            {data.scene?.number ? `Scene ${data.scene.number}` : `Scene #${data.scene?.order}`}
                        </span>
                    </div>
                    <h1
                        data-testid="beta-hm-scene-heading"
                        className={`text-2xl sm:text-3xl font-semibold mt-1 ${data.scene?.omitted ? "text-amber-300/70" : ""}`}
                    >
                        {data.scene?.heading}
                    </h1>
                    {data.scene?.omitted ? (
                        <p className="text-[11px] uppercase tracking-[0.24em] text-amber-400 mt-2">Omitted in latest revision</p>
                    ) : null}
                </header>

                <div className="max-w-3xl mx-auto space-y-4">
                    <section className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-4">
                        <h2 className="text-[11px] uppercase tracking-[0.24em] text-neutral-400">Characters in scene</h2>
                        {data.characters?.length ? (
                            <ul data-testid="beta-hm-scene-characters" className="mt-3 flex flex-wrap gap-1.5">
                                {data.characters.map((c) => (
                                    <li key={c.id}>
                                        <Link
                                            data-testid={`beta-hm-scene-character-${c.id}`}
                                            to={`/beta/projects/${projectId}/hm/characters/${c.id}`}
                                            className="tap inline-block text-[12px] text-neutral-200 border border-neutral-800 rounded-full px-2.5 py-1 hover:bg-neutral-900"
                                        >
                                            {c.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="mt-2 text-sm text-neutral-500">No characters listed for this scene.</p>
                        )}
                    </section>

                    {/* Iter-158.13.2.20f.42a — Scene-scoped H&M Working
                        Breakdown. Reads and writes via the canonical
                        Route 1 mount so the exact rows imported from
                        the immutable AI Breakdown (which were written
                        via the same canonical mount, without a scope
                        stamp) are visible and editable here. Manual
                        rows added from this Scene default-attach to
                        it and appear on the linked Character workspaces
                        too — ENTER-ONCE / RELATE / PRESERVE. */}
                    <DeptBreakdownSection
                        apiBase="/projects"
                        projectId={projectId}
                        scope={{ scene_id: sceneId }}
                    />
                </div>
            </div>
        </div>
    );
}
